/**
 * DSL Grammar - Definitions for field, object, and date operations
 */

/**
 * What one argument of a rule-DSL operation accepts.
 *
 * The rule DSL's own grammar, distinct from the schema DSL's in `dsl-grammar.ts`
 * despite the shared names - this one has no `date`, `lower` or `upper` argument
 * types, because rule operations take fields and values rather than date parts.
 */
export type DslArgumentType = 'field' | 'value' | 'expr' | 'code';

/** One argument of a rule-DSL operation: its name, type, and whether it is optional. */
export interface DslArgumentDefinition {
  name: string;
  type: DslArgumentType;
  description: string;
}

interface BaseOperationDefinition {
  category: 'field' | 'object' | 'dates';
  detail: string;
  description: string;
  insertText: string;
  args: readonly DslArgumentDefinition[];
  allowOptions?: boolean;
}

/** A rule-DSL operation applying to one field's value. */
export interface DslFieldOperationDefinition extends BaseOperationDefinition {
  category: 'field';
  name:
    | 'gte'
    | 'gt'
    | 'lte'
    | 'lt'
    | 'lteDateOnly'
    | 'gteDateOnly'
    | 'within'
    | 'withinHours'
    | 'withinMinutes'
    | 'requiredIf'
    | 'requiredIfValue'
    | 'notFutureDate'
    | 'refine'
    | 'eq'
    | 'neq';
  instruction:
    | {
        kind: 'cmp';
        op:
          | 'gte'
          | 'gt'
          | 'lte'
          | 'lt'
          | 'lteDateOnly'
          | 'gteDateOnly'
          | 'eq'
          | 'neq';
      }
    | { kind: 'within' }
    | { kind: 'withinHours' }
    | { kind: 'withinMinutes' }
    | { kind: 'requiredIf' }
    | { kind: 'requiredIfValue' }
    | { kind: 'notFutureDate' }
    | { kind: 'refine' };
}

/** A rule-DSL operation comparing fields with each other. */
export interface DslObjectOperationDefinition extends BaseOperationDefinition {
  category: 'object';
  name: 'refine';
  instruction: { kind: 'objectRefine' };
}

/** A rule-DSL operation over dates and times. */
export interface DslDatesOperationDefinition extends BaseOperationDefinition {
  category: 'dates';
  name: 'notFuture';
  instruction: { kind: 'datesNotFuture' };
  variadicArgs: boolean;
}

/**
 * @dslOperation gte
 * @category field
 * @detail Require field to be less than or equal to another field (FieldName <= OtherField)
 * @description Ensures FieldName does not exceed OtherField. Validates: FieldName <= OtherField. Note: Name derives from constraint attachment perspective.
 * @insertText field('FieldName').gte('OtherField') // FieldName >= OtherField
 * @arg otherField field Field that serves as the upper bound.
 * @instruction cmp gte
 */
export function dsl_field_gte() {}

/**
 * @dslOperation gt
 * @category field
 * @detail Require field to be strictly less than another field (FieldName < OtherField)
 * @description Ensures FieldName is below OtherField. Validates: FieldName < OtherField. Note: Name derives from constraint attachment perspective.
 * @insertText field('FieldName').gt('OtherField') // FieldName > OtherField
 * @arg otherField field Field that serves as the strict upper bound.
 * @instruction cmp gt
 */
export function dsl_field_gt() {}

/**
 * @dslOperation lte
 * @category field
 * @detail Require field to be greater than or equal to another field (FieldName >= OtherField)
 * @description Ensures FieldName meets or exceeds OtherField. Validates: FieldName >= OtherField. Note: Name derives from constraint attachment perspective.
 * @insertText field('FieldName').lte('OtherField') // FieldName <= OtherField
 * @arg otherField field Field that serves as the lower bound.
 * @instruction cmp lte
 */
export function dsl_field_lte() {}

/**
 * @dslOperation lt
 * @category field
 * @detail Require field to be strictly greater than another field (FieldName > OtherField)
 * @description Ensures FieldName exceeds OtherField. Validates: FieldName > OtherField. Note: Name derives from constraint attachment perspective.
 * @insertText field('FieldName').lt('OtherField') // FieldName < OtherField
 * @arg otherField field Field that serves as the strict lower bound.
 * @instruction cmp lt
 */
export function dsl_field_lt() {}

/**
 * @dslOperation eq
 * @category field
 * @detail Ensure two fields or a field and value are equal
 * @description Compare FieldName to OtherField or literal value for equality.
 * @insertText field('FieldName').eq('OtherField')
 * @arg otherField field Field or value to compare against.
 * @instruction cmp eq
 */
export function dsl_field_eq() {}

/**
 * @dslOperation neq
 * @category field
 * @detail Ensure two fields or a field and value are not equal
 * @description Compare FieldName to OtherField or literal value for inequality.
 * @insertText field('FieldName').neq('OtherField')
 * @arg otherField field Field or value to compare against.
 * @instruction cmp neq
 */
export function dsl_field_neq() {}

/**
 * @dslOperation lteDateOnly
 * @category field
 * @detail Ensure the left date is not after the right date (date-only)
 * @description Compare two date-only values and flag when FieldName occurs after OtherField.
 * @insertText field('FieldName').lteDateOnly('OtherField')
 * @arg otherField field Date field to compare against.
 * @instruction cmp lteDateOnly
 */
export function dsl_field_lteDateOnly() {}

/**
 * @dslOperation gteDateOnly
 * @category field
 * @detail Ensure the left date is strictly after the right date (date-only)
 * @description Compare two date-only values and flag when FieldName is not after OtherField.
 * @insertText field('FieldName').gteDateOnly('OtherField')
 * @arg otherField field Date field to compare against.
 * @instruction cmp gteDateOnly
 */
export function dsl_field_gteDateOnly() {}

/**
 * @dslOperation within
 * @category field
 * @detail Ensure a field falls within the inclusive bounds provided
 * @description Assert that FieldName stays between MinField and MaxField (inclusive).
 * @insertText field('FieldName').within('MinField','MaxField')
 * @arg minField field Lower bound field.
 * @arg maxField field Upper bound field.
 * @instruction within
 */
export function dsl_field_within() {}

/**
 * @dslOperation withinHours
 * @category field
 * @detail Ensure two datetime fields are within N hours of each other
 * @description Validates that the time difference between FieldName and OtherField does not exceed the specified number of hours.
 * @insertText field('FieldName').withinHours('OtherField', 24)
 * @arg otherField field Datetime field to compare against.
 * @arg hours value Maximum number of hours difference allowed.
 * @instruction withinHours
 */
export function dsl_field_withinHours() {}

/**
 * @dslOperation withinMinutes
 * @category field
 * @detail Ensure two datetime fields are within N minutes of each other
 * @description Validates that the time difference between FieldName and OtherField does not exceed the specified number of minutes.
 * @insertText field('FieldName').withinMinutes('OtherField', 30)
 * @arg otherField field Datetime field to compare against.
 * @arg minutes value Maximum number of minutes difference allowed.
 * @instruction withinMinutes
 */
export function dsl_field_withinMinutes() {}

/**
 * @dslOperation requiredIf
 * @category field
 * @detail Mark a field as required when another field is present
 * @description Require FieldName when OtherField has a value.
 * @insertText field('FieldName').requiredIf('OtherField')
 * @arg otherField field Field whose presence should trigger the requirement.
 * @instruction requiredIf
 */
export function dsl_field_requiredIf() {}

/**
 * @dslOperation requiredIfValue
 * @category field
 * @detail Require a field when another field matches a value
 * @description Require FieldName when OtherField equals the supplied Value.
 * @insertText field('FieldName').requiredIfValue('OtherField','Value')
 * @arg otherField field Field to inspect.
 * @arg value value Value that triggers the requirement.
 * @instruction requiredIfValue
 */
export function dsl_field_requiredIfValue() {}

/**
 * @dslOperation notFutureDate
 * @category field
 * @detail Ensure the date-only value is not in the future
 * @description Validate that FieldName (a date) is today or earlier.
 * @insertText field('FieldName').notFutureDate()
 * @instruction notFutureDate
 */
export function dsl_field_notFutureDate() {}

/**
 * @dslOperation refine
 * @category field
 * @detail Run a custom expression for a specific field
 * @description Attach a bespoke expression-based rule to FieldName.
 * @insertText field('FieldName').refine('expr(root.FieldName)')
 * @arg expression expr Expression to evaluate against the root object.
 * @instruction refine
 */
export function dsl_field_refine() {}

/**
 * @dslOperation refine
 * @category object
 * @detail Apply a schema-level refinement with a code and expression
 * @description Define a schema-wide check identified by RuleCode using an expression.
 * @insertText object.refine('RuleCode','expr(root)')
 * @arg code code Stable identifier for the refinement.
 * @arg expression expr Expression evaluated against the root object.
 * @instruction objectRefine
 */
export function dsl_object_refine() {}

/**
 * @dslOperation notFuture
 * @category dates
 * @detail Assert a group of dates are not in the future
 * @description Ensure each listed date is today or earlier.
 * @insertText dates('StartDate','EndDate').notFuture()
 * @instruction datesNotFuture
 * @variadicArgs true
 */
export function dsl_dates_notFuture() {}
